import { useEffect, useState, useRef } from 'react';
import {
  Box, Typography, IconButton, CircularProgress,
  ImageList, ImageListItem, ImageListItemBar,
  Dialog, DialogContent, Button, TextField, Alert,
  Tooltip,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import api from '../services/api';

export default function GaleriaFotos({
  contexto,
  membro_equipe_id,
  checkin_id,
  acompanhamento_id,
  indicador_id,
  item_relatorio_id,
  projeto_id,
  titulo = 'Fotos',
  somenteLeitura = false,
}) {
  const [fotos, setFotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fotoAberta, setFotoAberta] = useState(null);
  const [descricao, setDescricao] = useState('');
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  const inputRef = useRef(null);

  // "Álbum do projeto": quando só projeto_id é passado (sem contexto específico),
  // a busca traz TUDO relacionado ao projeto — mas toda foto NOVA enviada por aqui
  // entra sempre como álbum livre, já que não há como saber a qual indicador/relatório
  // uma foto solta pertenceria.
  const modoAlbumProjeto = Boolean(projeto_id) && !contexto;

  const paramsBusca = modoAlbumProjeto
    ? { projeto_id }
    : {
        contexto,
        ...(membro_equipe_id && { membro_equipe_id }),
        ...(checkin_id && { checkin_id }),
        ...(acompanhamento_id && { acompanhamento_id }),
        ...(indicador_id && { indicador_id }),
        ...(item_relatorio_id && { item_relatorio_id }),
      };

  const carregarFotos = () => {
    api.get('/fotos/', { params: paramsBusca }).then((res) => {
      setFotos(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => {
    carregarFotos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contexto, membro_equipe_id, checkin_id, acompanhamento_id, indicador_id, item_relatorio_id, projeto_id]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setErro('');
    setSucesso('');

    let sucessos = 0;
    let falhas = 0;

    for (const file of files) {
      const formData = new FormData();
      formData.append('imagem', file);
      formData.append('descricao', descricao);

      if (modoAlbumProjeto) {
        formData.append('contexto', 'projeto');
        formData.append('projeto', projeto_id);
      } else {
        formData.append('contexto', contexto);
        if (membro_equipe_id) formData.append('membro_equipe', membro_equipe_id);
        if (checkin_id) formData.append('checkin', checkin_id);
        if (acompanhamento_id) formData.append('acompanhamento', acompanhamento_id);
        if (indicador_id) formData.append('indicador', indicador_id);
        if (item_relatorio_id) formData.append('item_relatorio', item_relatorio_id);
      }

      try {
        await api.post('/fotos/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        sucessos += 1;
      } catch {
        falhas += 1;
      }
    }

    if (sucessos > 0 && falhas === 0) {
      setSucesso(sucessos === 1 ? 'Foto enviada com sucesso!' : `${sucessos} fotos enviadas com sucesso!`);
    } else if (sucessos > 0 && falhas > 0) {
      setSucesso(`${sucessos} de ${sucessos + falhas} foto(s) enviada(s).`);
      setErro(`${falhas} foto(s) não puderam ser enviadas.`);
    } else {
      setErro('Erro ao enviar a(s) foto(s). Confira o console/terminal para mais detalhes.');
    }

    setDescricao('');
    if (sucessos > 0) carregarFotos();
    e.target.value = '';
  };

  const handleDeletar = async (id) => {
    try {
      await api.delete(`/fotos/${id}/`);
      setFotos(fotos.filter((f) => f.id !== id));
    } catch {
      setErro('Erro ao deletar foto.');
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="subtitle1" fontWeight={600}>
          {titulo} {fotos.length > 0 && `(${fotos.length})`}
        </Typography>
        {!somenteLeitura && (
          <Box display="flex" alignItems="center" gap={1}>
            <TextField
              size="small"
              placeholder="Descrição (opcional)"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              sx={{ width: 200 }}
            />
            <Tooltip title="Adicionar fotos">
              <IconButton
                onClick={() => inputRef.current.click()}
                sx={{ backgroundColor: '#1a1a2e', color: 'white', '&:hover': { backgroundColor: '#e94560' } }}
              >
                <AddPhotoAlternateIcon />
              </IconButton>
            </Tooltip>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={handleUpload}
            />
          </Box>
        )}
      </Box>

      {erro && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro('')}>{erro}</Alert>}
      {sucesso && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSucesso('')}>{sucesso}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={24} />
        </Box>
      ) : fotos.length === 0 ? (
        <Box
          sx={{
            border: '2px dashed #ccc',
            borderRadius: 2,
            py: 4,
            textAlign: 'center',
            cursor: somenteLeitura ? 'default' : 'pointer',
          }}
          onClick={() => !somenteLeitura && inputRef.current.click()}
        >
          <AddPhotoAlternateIcon sx={{ fontSize: 40, color: '#ccc' }} />
          <Typography variant="body2" color="text.secondary" mt={1}>
            {somenteLeitura ? 'Nenhuma foto' : 'Clique para adicionar fotos'}
          </Typography>
        </Box>
      ) : (
        <ImageList cols={3} gap={8}>
          {fotos.map((foto) => (
            <ImageListItem key={foto.id} sx={{ borderRadius: 1, overflow: 'hidden' }}>
              <img
                src={foto.imagem_url}
                alt={foto.descricao || 'Foto'}
                style={{ height: 160, objectFit: 'cover', width: '100%' }}
              />
              <ImageListItemBar
                title={foto.descricao || (modoAlbumProjeto ? foto.origem_display : '')}
                subtitle={modoAlbumProjeto ? `${foto.enviado_por_nome} · ${foto.origem_display}` : foto.enviado_por_nome}
                actionIcon={
                  <Box>
                    <IconButton
                      size="small"
                      sx={{ color: 'white' }}
                      onClick={() => setFotoAberta(foto)}
                    >
                      <ZoomInIcon fontSize="small" />
                    </IconButton>
                    {!somenteLeitura && (
                      <IconButton
                        size="small"
                        sx={{ color: 'white' }}
                        onClick={() => handleDeletar(foto.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                }
              />
            </ImageListItem>
          ))}
        </ImageList>
      )}

      <Dialog open={Boolean(fotoAberta)} onClose={() => setFotoAberta(null)} maxWidth="md" fullWidth>
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            onClick={() => setFotoAberta(null)}
            sx={{ position: 'absolute', top: 8, right: 8, backgroundColor: 'rgba(0,0,0,0.5)', color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
          {fotoAberta && (
            <img
              src={fotoAberta.imagem_url}
              alt={fotoAberta.descricao || 'Foto'}
              style={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }}
            />
          )}
          <Box sx={{ p: 2 }}>
            {fotoAberta?.descricao && <Typography variant="body2">{fotoAberta.descricao}</Typography>}
            <Typography variant="caption" color="text.secondary" display="block">
              Enviado por: {fotoAberta?.enviado_por_nome}
            </Typography>
            {modoAlbumProjeto && (
              <Typography variant="caption" color="text.secondary" display="block">
                Origem: {fotoAberta?.origem_display}
              </Typography>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}